/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MarkdownRenderer } from '@/components/ui/markdown-renderer';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import {
  Copy,
  Check,
  Download,
  Save,
  FileText,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import {
  getTestingPlanContent,
  type TestingPlanSummary,
} from '../../../services/skillshop';

export interface TestingPlanViewerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plans: TestingPlanSummary[];
  initialFileName?: string;
  repoUrl?: string;
  repoPath?: string | null;
}

interface PlanContentState {
  fileName: string;
  content: string;
  modifiedAt?: string;
  filePath?: string;
}

/**
 * Whether the current environment supports the File System Access API
 * `showSaveFilePicker`, which gives users a native "Save As" dialog letting
 * them choose where to save the testing plan.
 */
function supportsSaveFilePicker(): boolean {
  return typeof window !== 'undefined' && typeof (window as any).showSaveFilePicker === 'function';
}

function formatTimestamp(value?: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function describePlan(plan: TestingPlanSummary): string {
  if (plan.scope === 'release' && plan.releaseId) {
    return `Release ${plan.releaseId}`;
  }
  return 'Full Product';
}

export function TestingPlanViewer({
  open,
  onOpenChange,
  plans,
  initialFileName,
  repoUrl,
  repoPath,
}: TestingPlanViewerProps) {
  const { toast } = useToast();
  const [selectedFileName, setSelectedFileName] = useState<string>('');
  const [planContent, setPlanContent] = useState<PlanContentState | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const sortedPlans = useMemo(
    () =>
      [...plans].sort((a, b) => b.modifiedAt.localeCompare(a.modifiedAt)),
    [plans]
  );

  useEffect(() => {
    if (!open) return;
    if (sortedPlans.length === 0) {
      setSelectedFileName('');
      return;
    }
    const preferred = initialFileName && sortedPlans.find(p => p.fileName === initialFileName)
      ? initialFileName
      : sortedPlans[0].fileName;
    setSelectedFileName(preferred);
  }, [open, initialFileName, sortedPlans]);

  useEffect(() => {
    if (!open || !selectedFileName) {
      setPlanContent(null);
      setLoadError(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setLoadError(null);

    getTestingPlanContent(selectedFileName, repoUrl, repoPath ?? undefined)
      .then(result => {
        if (cancelled) return;
        if (result.success && typeof result.content === 'string') {
          setPlanContent({
            fileName: result.fileName ?? selectedFileName,
            content: result.content,
            modifiedAt: result.modifiedAt,
            filePath: result.filePath,
          });
        } else {
          setPlanContent(null);
          setLoadError(result.error || 'Failed to load testing plan.');
        }
      })
      .catch(error => {
        if (cancelled) return;
        setPlanContent(null);
        setLoadError(error?.message || 'Failed to load testing plan.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, selectedFileName, repoUrl, repoPath]);

  const handleCopy = async () => {
    if (!planContent) return;
    try {
      await navigator.clipboard.writeText(planContent.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error: any) {
      toast({
        title: 'Copy failed',
        description: error?.message || 'Unable to copy testing plan to clipboard.',
        variant: 'destructive',
      });
    }
  };

  const downloadFallback = (fileName: string, content: string) => {
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSaveAs = async () => {
    if (!planContent) return;
    setIsSaving(true);
    try {
      if (supportsSaveFilePicker()) {
        try {
          const handle = await (window as any).showSaveFilePicker({
            suggestedName: planContent.fileName,
            types: [
              {
                description: 'Markdown file',
                accept: { 'text/markdown': ['.md'] },
              },
            ],
          });
          const writable = await handle.createWritable();
          await writable.write(planContent.content);
          await writable.close();
          toast({
            title: 'Testing plan saved',
            description: `Saved ${planContent.fileName} to your chosen location.`,
          });
          return;
        } catch (error: any) {
          if (error?.name === 'AbortError') {
            return;
          }
          console.warn('[TestingPlanViewer] showSaveFilePicker failed, falling back to download:', error);
        }
      }

      downloadFallback(planContent.fileName, planContent.content);
      toast({
        title: 'Testing plan downloaded',
        description: `Downloaded ${planContent.fileName} to your browser's default download folder.`,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const selectedPlan = sortedPlans.find(p => p.fileName === selectedFileName);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Testing Plan
          </DialogTitle>
          <DialogDescription>
            Review the generated testing plan and save it to your computer.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 min-h-0 flex-1">
          {sortedPlans.length === 0 ? (
            <div className="flex items-center gap-2 p-4 rounded-md border bg-muted/40 text-sm text-muted-foreground">
              <AlertCircle className="h-4 w-4" />
              <span>No testing plans have been generated yet for this product.</span>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1">
                <Select value={selectedFileName} onValueChange={setSelectedFileName}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a testing plan" />
                  </SelectTrigger>
                  <SelectContent>
                    {sortedPlans.map(plan => (
                      <SelectItem key={plan.fileName} value={plan.fileName}>
                        <div className="flex items-center gap-2">
                          <span>{plan.fileName}</span>
                          <Badge variant="outline" className="text-xs">
                            {describePlan(plan)}
                          </Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedPlan && (
                <div className="text-xs text-muted-foreground">
                  Last updated {formatTimestamp(selectedPlan.modifiedAt)}
                </div>
              )}
            </div>
          )}

          {isLoading && (
            <div className="flex items-center justify-center p-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Loading testing plan...
            </div>
          )}

          {!isLoading && loadError && (
            <div className="flex items-start gap-2 p-3 rounded-md border bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
              <AlertCircle className="h-4 w-4 mt-0.5" />
              <div>{loadError}</div>
            </div>
          )}

          {!isLoading && !loadError && planContent && (
            <Tabs defaultValue="preview" className="flex flex-col min-h-0 flex-1">
              <TabsList className="self-start">
                <TabsTrigger value="preview">Preview</TabsTrigger>
                <TabsTrigger value="raw">Raw Markdown</TabsTrigger>
              </TabsList>
              <TabsContent value="preview" className="mt-3 min-h-0 flex-1">
                <ScrollArea className="h-[55vh] rounded-md border p-4 bg-background">
                  <MarkdownRenderer content={planContent.content} />
                </ScrollArea>
              </TabsContent>
              <TabsContent value="raw" className="mt-3 min-h-0 flex-1">
                <ScrollArea className="h-[55vh] rounded-md border bg-muted/30">
                  <pre className="p-4 text-xs whitespace-pre-wrap break-words font-mono">
                    {planContent.content}
                  </pre>
                </ScrollArea>
              </TabsContent>
            </Tabs>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={handleCopy} disabled={!planContent}>
            {copied ? (
              <>
                <Check className="h-4 w-4 mr-2 text-green-600" />
                Copied
              </>
            ) : (
              <>
                <Copy className="h-4 w-4 mr-2" />
                Copy
              </>
            )}
          </Button>
          <Button onClick={handleSaveAs} disabled={!planContent || isSaving} className="gap-2">
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : supportsSaveFilePicker() ? (
              <>
                <Save className="h-4 w-4" />
                Save As...
              </>
            ) : (
              <>
                <Download className="h-4 w-4" />
                Download
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
